import {
    Unsubscribe,
    get,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
    onValue,
} from '@/features/firebase/adapter/database'
import FirebaseDatabaseRefService from '@/features/firebase/services/database-ref'
import {
    FirebaseOpenTabSummary,
    firebaseOpenTabSummarySchema,
} from '@/features/openTabs/hooks/useFirebaseOpenTabs/schema'
import { logger } from '@/features/openTabs/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import isEqual from 'lodash.isequal'
import { useCallback, useEffect, useRef, useState } from 'react'

export const useFirebaseOpenTabs = () => {
    const handleError = useHandleErrorWithToast(logger)
    const [tabs, setTabs] = useState<FirebaseOpenTabSummary[]>([])
    const [revisions, setRevisions] = useState<Readonly<Record<string, number>>>({})
    const sequence = useRef(0)
    const [loading, setLoading] = useState(true)
    const [failed, setFailed] = useState(false)
    const [revision, setRevision] = useState(0)
    const refreshRef = useRef<() => Promise<void>>(async () => {})
    const retry = useCallback(() => setRevision((value) => value + 1), [])
    const refresh = useCallback(() => refreshRef.current(), [])

    useEffect(() => {
        let active = true
        let initialized = false
        let subscriptionFailed = false
        const invalidKeys = new Set<string>()
        const recordRevisions: Record<string, number> = {}
        const records = new Map<string, FirebaseOpenTabSummary>()
        const unsubscribes: Unsubscribe[] = []
        setLoading(true)
        setFailed(false)

        const publish = () => {
            setRevisions({ ...recordRevisions })
            const next = [...records.values()].sort((left, right) =>
                left.uuid.localeCompare(right.uuid)
            )
            setTabs((previous) => (isEqual(previous, next) ? previous : next))
        }
        const handleSubscriptionError = (error: unknown) => {
            if (!active) return
            subscriptionFailed = true
            setFailed(true)
            setLoading(false)
            void handleError(error)
        }

        try {
            const reference = FirebaseDatabaseRefService.openTabs()
            const upsert = (snapshot: Parameters<Parameters<typeof onChildAdded>[1]>[0]) => {
                if (!active || !snapshot.key) return
                const result = firebaseOpenTabSummarySchema.safeParse(snapshot.val())
                if (!result.success || result.data.uuid !== snapshot.key) {
                    records.delete(snapshot.key)
                    recordRevisions[snapshot.key] = ++sequence.current
                    if (initialized) publish()
                    invalidKeys.add(snapshot.key)
                    setFailed(true)
                    void handleError(
                        result.success
                            ? new Error('Open tab UUID does not match its key')
                            : result.error
                    )
                    return
                }
                invalidKeys.delete(snapshot.key)
                setFailed(subscriptionFailed || invalidKeys.size > 0)
                recordRevisions[snapshot.key] = ++sequence.current
                const previous = records.get(snapshot.key)
                if (!isEqual(previous, result.data)) records.set(snapshot.key, result.data)
                if (initialized) publish()
            }
            unsubscribes.push(onChildAdded(reference, upsert, handleSubscriptionError))
            unsubscribes.push(onChildChanged(reference, upsert, handleSubscriptionError))
            unsubscribes.push(
                onChildRemoved(
                    reference,
                    (snapshot) => {
                        if (!active || !snapshot.key) return
                        records.delete(snapshot.key)
                        invalidKeys.delete(snapshot.key)
                        setFailed(subscriptionFailed || invalidKeys.size > 0)
                        recordRevisions[snapshot.key] = ++sequence.current
                        if (initialized) publish()
                    },
                    handleSubscriptionError
                )
            )
            // Initial child events precede this barrier; its snapshot must not overwrite later updates.
            unsubscribes.push(
                onValue(
                    reference,
                    () => {
                        if (!active) return
                        initialized = true
                        publish()
                        setLoading(false)
                    },
                    handleSubscriptionError,
                    { onlyOnce: true }
                )
            )
            refreshRef.current = async () => {
                try {
                    await get(reference)
                } catch (error) {
                    handleSubscriptionError(error)
                    throw error
                }
            }
        } catch (error) {
            handleSubscriptionError(error)
        }

        return () => {
            active = false
            refreshRef.current = async () => {}
            unsubscribes.forEach((unsubscribe) => unsubscribe())
        }
    }, [handleError, revision])

    return { tabs, revisions, loading, failed, retry, refresh }
}
